/**
 * FlashBook — the platform's audio engine.
 *
 * The audio desk is written against the Web Audio API: it decodes a clip into an
 * `AudioBuffer`, renders the tone-and-space half of an edit through a node graph
 * in an `OfflineAudioContext`, and hands the result back as a URL a player can
 * reach. Two of those three are the browser's alone, and this module is where the
 * desk asks *what can this platform do* instead of testing for a global and
 * hiding its Edit control when the global is missing.
 *
 *  - **web** (`flashbookAudioEngineImpl.ts`) — the platform's own implementation.
 *    Nothing is polyfilled; the graph is exactly what it always was.
 *  - **native** (`flashbookAudioEngineImpl.native.ts`) — `react-native-audio-api`,
 *    the Web Audio API implemented for iOS and Android, plus a file for a
 *    rendered clip, because React Native has no `URL.createObjectURL`.
 *
 * One deliberate asymmetry, and it is why this contract needs no capability
 * flags: `DynamicsCompressorNode` is the single node that library does not
 * implement (its coverage table still lists it as missing), so *Evenness* is a
 * sample pass in `flashbookAudioEditor` on **every** platform. One
 * implementation, identical output everywhere, and testable without a sound card.
 */
import {createPlatformEngine} from '#/lib/flashbookAudioEngineImpl'

export type AudioEngine = {
  /** Whether this build can decode and render a clip at all. */
  available: () => boolean
  createContext: () => AudioContext
  createOfflineContext: (
    channels: number,
    length: number,
    sampleRate: number,
  ) => OfflineAudioContext
  /**
   * Rendered PCM as something a player can reach.
   *
   * `key` identifies the edit, so a platform that has to write a file can reuse
   * the same one when a creator re-renders the same settings instead of leaving a
   * trail of megabytes behind. The web implementation ignores it — a blob URL is
   * already keyed by nothing but itself.
   */
  persistClip: (wav: ArrayBuffer, key: string) => Promise<string>
  /** Drops a rendered clip's bytes once the desk evicts it from its cache. */
  releaseClip: (url: string) => void
  /**
   * The bytes of a clip that `fetch` cannot serve — a `blob:` the browser is
   * holding, or a file the device wrote. Throws when this platform genuinely
   * cannot read it, which is an answer rather than a failure.
   */
  readLocalClip: (uri: string) => Promise<ArrayBuffer>
}

/** The platform's engine, built once. */
const platformEngine: AudioEngine = createPlatformEngine()

/** Testing seam — the desk's engine is a platform fact, so tests replace it. */
let override: AudioEngine | null = null

export function setAudioEngine(engine: AudioEngine | null): void {
  override = engine
}

export function getAudioEngine(): AudioEngine {
  return override ?? platformEngine
}
